import { HTMLAttributes, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import styled from '@emotion/styled';
import { initEcharts } from 'lib/echarts';
import { useSize } from 'ahooks';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';

export interface DashboardChatProps extends HTMLAttributes<HTMLDivElement> {
  loading: boolean;
  data: Remind.ProfileRecord[];
  range: [number, number];
}

const Container = styled.div`
  height: 400px;
`;

const colors = ['#5B8FF9', '#5AD8A6', '#F6BD16'];

const splitNumber = 5;

function getChartMaxValue(data: number[], splitNumber: number) {
  const max = Math.max(...data);

  return Math.ceil(max / splitNumber) * splitNumber;
}

function DashboardChat(props: DashboardChatProps) {
  const { loading, data: wasteServiceData, range, ...otherProps } = props;

  const data = useMemo(() => {
    const res = [];

    const normalData = wasteServiceData.map((item) => {
      return {
        ...item,
        avgElapsedTime: Math.ceil(parseInt(item.avgElapsedTime, 10) / 60 / 1000),
        date: parseInt(item.date, 10),
      };
    });

    const [start, end] = range;

    let flag = start;

    while (flag < end) {
      if (normalData[0] && normalData[0].date === flag) {
        const current = normalData.shift();

        if (current) {
          res.push(current);
        }
      } else {
        res.push({
          remindCount: 0,
          solveCount: 0,
          date: flag,
          avgElapsedTime: 0,
        });
      }

      flag += 24 * 60 * 60 * 1000;
    }

    return res;
  }, [wasteServiceData, range]);

  const { t } = useTranslate();

  const option = useMemo(() => {
    return {
      color: colors,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999',
          },
        },
      },
      grid: {
        containLabel: true,
        left: 20,
        right: 20,
        bottom: 35,
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            alignWithLabel: true,
          },
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          position: 'left',
        },
        {
          type: 'value',
        },
      ],
      series: [
        {
          name: t('remind.强提醒会话数'),
          type: 'bar',
        },
        {
          name: t('remind.已解决会话数'),
          type: 'bar',
        },
        {
          name: t('remind.平均解决时长（分钟）'),
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
        },
      ],
    };
  }, [t]);

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();

  const { width: containerWidth, height: containerHeight } = useSize(containerRef) || {};

  const refsCurrent = {
    option,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const updateOption = useMemo(() => {
    const remindCountData = data.map(({ remindCount }) => remindCount);
    const avgTimeData = data.map(({ avgElapsedTime }) => avgElapsedTime);

    const remindCountMax = getChartMaxValue(remindCountData, splitNumber);
    const avgTimeMax = getChartMaxValue(avgTimeData, splitNumber);

    return {
      legend: {
        itemGap: 20,
        data: [
          {
            icon: 'rect',
            name: t('remind.强提醒会话数'),
          },
          {
            icon: 'rect',
            name: t('remind.已解决会话数'),
          },
          t('remind.平均解决时长（分钟）'),
        ],
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            alignWithLabel: true,
          },
          data: data.map(({ date }) => dayjs(date).format('MM-DD')),
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          position: 'left',
          max: remindCountMax,
          splitNumber,
          interval: remindCountMax / splitNumber,
        },
        {
          type: 'value',
          max: avgTimeMax,
          splitNumber,
          interval: avgTimeMax / splitNumber,
        },
      ],
      series: [
        {
          name: t('remind.强提醒会话数'),
          type: 'bar',
          data: remindCountData,
        },
        {
          name: t('remind.已解决会话数'),
          type: 'bar',
          data: data.map(({ solveCount }) => solveCount),
        },
        {
          name: t('remind.平均解决时长（分钟）'),
          type: 'line',
          data: avgTimeData,
          yAxisIndex: 1,
        },
      ],
    };
  }, [data, t]);

  useEffect(() => {
    const { current: container } = containerRef;

    if (!container) {
      return;
    }

    const {
      current: { option },
    } = refs;

    const chart = initEcharts(container);
    chartRef.current = chart;
    chart.setOption(option);
  }, []);

  useLayoutEffect(() => {
    const { current: chart } = chartRef;

    if (!chart) {
      return;
    }

    chart.resize();
  }, [containerWidth, containerHeight]);

  useEffect(() => {
    const { current: chart } = chartRef;

    if (!chart) {
      return;
    }

    chart.setOption(updateOption);
  }, [updateOption]);

  useEffect(() => {
    const { current: chart } = chartRef;

    if (!chart) {
      return;
    }

    if (loading) {
      chart.showLoading();
    } else {
      chart.hideLoading();
    }
  }, [loading]);

  return <Container ref={containerRef} {...otherProps} />;
}

export default DashboardChat;
